import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from "../config/api";


// Module definitions
export const MODULES = {
  tracking: { name: 'Container Tracking', icon: 'Ship' },
  lcl: { name: 'LCL Tracking', icon: 'Package' },
  port_tracking: { name: 'Port Tracking', icon: 'Anchor' },
  trucking: { name: 'Trucking', icon: 'Truck' },
  rate_finder: { name: 'Rate Finder', icon: 'Search' },
  vendor_ap: { name: 'Vendor / AP Intelligence', icon: 'FileText' },
  ar_credits: { name: 'AR & Credits', icon: 'CreditCard' },
  bank_status: { name: 'Bank Status', icon: 'Building' },
  loans: { name: 'Bank Loans', icon: 'Landmark' },
  cashflow: { name: 'Cash Flow Pro', icon: 'TrendingUp' },
  izvrsbe: { name: 'Izvršbe', icon: 'Gavel' },
  sales_leads: { name: 'Sales Leads', icon: 'Users' },
  hr_performance: { name: 'HR Performance', icon: 'Award' },
  tools: { name: 'Free Tools', icon: 'Wrench' },
};

// Free modules that don't require subscription
const FREE_MODULES = ['tools'];

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessCache, setAccessCache] = useState({});

  // Fetch subscription on mount and when user changes
  useEffect(() => {
    if (user && token) {
      fetchSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [user, token]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has access to a specific module
  const hasModuleAccess = useCallback((moduleId) => {
    // Free modules are always accessible
    if (FREE_MODULES.includes(moduleId)) {
      return true;
    }

    // Admin users have full access (for development/testing)
    if (user?.role === 'admin' || user?.is_admin) {
      return true;
    }

    // Check subscription
    if (!subscription || subscription.status !== 'active') {
      return false;
    }

    return subscription.modules?.includes(moduleId) || false;
  }, [subscription, user]);

  // Check access and return detailed info
  const checkAccess = useCallback(async (moduleId) => {
    // Check cache first
    if (accessCache[moduleId]) {
      return accessCache[moduleId];
    }

    // Quick checks
    if (FREE_MODULES.includes(moduleId)) {
      return { hasAccess: true, module: moduleId, reason: 'free' };
    }

    if (user?.role === 'admin' || user?.is_admin) {
      return { hasAccess: true, module: moduleId, reason: 'admin' };
    }

    try {
      const response = await fetch(
        `${API_URL}/subscriptions/check-access/${moduleId}?company_id=${user?.company_id || ''}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      if (response.ok) {
        const data = await response.json();
        const result = {
          hasAccess: data.has_access,
          module: moduleId,
          moduleName: data.module_name,
          reason: data.has_access ? 'subscribed' : 'no_subscription'
        };
        
        // Cache the result
        setAccessCache(prev => ({ ...prev, [moduleId]: result }));
        return result;
      }
    } catch (error) {
      console.error('Access check failed:', error);
    }

    return { hasAccess: false, module: moduleId, reason: 'error' };
  }, [user, token, accessCache]);

  // Get list of accessible modules
  const getAccessibleModules = useCallback(() => {
    const accessible = [...FREE_MODULES];
    
    if (user?.role === 'admin' || user?.is_admin) {
      return Object.keys(MODULES);
    }

    if (subscription?.modules) {
      accessible.push(...subscription.modules);
    }

    return [...new Set(accessible)];
  }, [subscription, user]);

  // Refresh subscription data
  const refreshSubscription = useCallback(() => {
    setAccessCache({});
    return fetchSubscription();
  }, []);

  const value = {
    subscription,
    loading,
    hasModuleAccess,
    checkAccess,
    getAccessibleModules,
    refreshSubscription,
    MODULES
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
